import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { spots as spotsMath } from '../../services/investmentCalculations';

import Colors from '../../../../../themes/Colors';
const DiscountProgressBar = ({
  promiseShows = 0,
  discounts,
  campaignDays = 0,
  discountType = 'daily',
  totalSpots = 0,
  originalCpm = 0,
  currency = 'USD'
}) => {

  const { t } = useTranslation();

  // For "total" discounts the thresholds (min_impressions) are total-campaign
  // spots, so compare against the campaign total instead of spots per day.
  const isTotal = discountType === 'total';
  const currentValue = isTotal ? totalSpots : promiseShows;

  const maxSpots =
    discounts && discounts.length ? discounts[discounts.length - 1].min_impressions || 0 : 0;
  const percentage = (currentValue / maxSpots) * 100;
  // Find next discount level
  const nextDiscount = discounts.find(d => currentValue < d.min_impressions);
  // "spots needed" expressed in both daily and total terms for the legend
  let dailySpotsNeeded = 0;
  let totalSpotsNeeded = 0;
  if (nextDiscount) {
    if (isTotal) {
      totalSpotsNeeded = nextDiscount.min_impressions - totalSpots;
      dailySpotsNeeded = campaignDays > 0 ? Math.ceil(totalSpotsNeeded / campaignDays) : 0;
    } else {
      dailySpotsNeeded = nextDiscount.min_impressions - promiseShows;
      totalSpotsNeeded = campaignDays > 0 ? Math.ceil(dailySpotsNeeded * campaignDays) : 0;
    }
  }
  const isMaxReached = !nextDiscount;

  // Thresholds and progress are spot-based; the legend shows what they cost.
  const toInvestment = spotsValue => spotsMath.toBudget(spotsValue, originalCpm);
  // `CODE $amount`, the same shape the investment warnings in this step use.
  const moneyFormat = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const toMoney = spotsValue => `${currency} $${moneyFormat.format(toInvestment(spotsValue))}`;

  return (
    <div style={{ width: '100%', padding: '8px 0' }}>
      {/* Legend */}
      <div
        style={{
          width: '90%',
          margin: '12px auto 12px',
          textAlign: 'center',
          fontSize: '14px',
          lineHeight: '1.5'
        }}
      >
        {isMaxReached ? (
          <span style={{ color: '#52C41A', fontWeight: 600 }}>
            {t('You reached the maximum benefits possible for this screen!')}
          </span>
        ) : (
          <span style={{ color: Colors.textSecondary }}>
            <Trans
              i18nKey={
                isTotal
                  ? 'Add {{investment}} total investment on this screen and get more benefits'
                  : 'Add {{investment}} daily investment on this screen and get more benefits'
              }
              values={{
                dailyInvestment: toMoney(dailySpotsNeeded),
                totalInvestment: toMoney(totalSpotsNeeded)
              }}
              components={{
                investment: <span style={{ color: '#52C41A', fontWeight: 600 }} />,
                span: <span style={{ color: '#52C41A', fontWeight: 600 }} />
              }}
            />
          </span>
        )}
      </div>
      {/* Progress Bar Container */}
      <div
        style={{
          position: 'relative',
          width: '90%',
          margin: '0 auto',
          height: '16px',
          backgroundColor: Colors.snow,
          borderRadius: '10px',
          overflow: 'visible',
          boxShadow: '0 0 0 3px #F0F0F0'
        }}
      >
        {/* Progress Fill */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${Math.min(percentage, 100)}%`,
            background: '#52C41A',
            borderRadius: '10px',
            transition: 'width 0.4s ease-in-out',
            zIndex: 1
          }}
        />
        {/* Milestone Markers */}
        {discounts.map((discount, index) => {
          const position = (discount.min_impressions / maxSpots) * 100;
          const reached = currentValue >= discount.min_impressions;
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                left: `${position}%`,
                top: '0',
                transform: 'translateX(-50%)',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              {/* Marker Line */}
              <div
                style={{
                  width: '3px',
                  height: '16px',
                  backgroundColor: reached ? '#F0F0F0' : '#F0F0F0',
                  borderRadius: '2px',
                  transition: 'background-color 0.3s ease'
                }}
              />
              {/* Marker Label - Subtle Number */}
              <div
                style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: '#135200',
                  fontWeight: reached ? 900 : 200,
                  whiteSpace: 'nowrap',
                  transition: 'color 0.3s ease'
                }}
              >
                {toMoney(discount.min_impressions)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DiscountProgressBar;
